import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { useAuthState } from 'modules/Auth/AuthContext';
import { apiListNotification, apiMarkNotificationsAsRead, apiFetchNotificationById, apiDeleteNotifications } from 'api/notification';
import { queryKeys } from 'utils/react-query';

interface NotificationApiOptions {
  unreadOnly?: boolean;
  enabled?: boolean;
}

interface MarkAsReadOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

interface DeleteOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

export const useNotificationApi = (config: NotificationApiOptions = {}) => {
  const { unreadOnly = true, enabled = true } = config;

  const { authData } = useAuthState();
  const userId = useCurrentUserId();
  const queryClient = useQueryClient();
  const sessionToken = authData?.session_token;

  const {
    data: notificationsData,
    isLoading,
    isError,
    error,
    refetch: refetchNotifications,
  } = useQuery(
    [queryKeys.Notifications, userId, { unread_only: unreadOnly }],
    () => apiListNotification({ sessionToken, userId, params: { unread_only: unreadOnly } }),
    {
      enabled: enabled && Boolean(userId && sessionToken),
      refetchInterval: 60000, // 1 minutes
      refetchIntervalInBackground: false,
    },
  );

  const {
    mutate: markAsReadMutate,
    isLoading: isMarkingAsRead,
    error: markAsReadError,
  } = useMutation({
    mutationFn: (notificationIds: string[]) => {
      const notifications = notificationIds.map((id) => ({ id, is_read: true }));
      return apiMarkNotificationsAsRead({ sessionToken, userId, notifications });
    },
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.Notifications, userId, { unread_only: unreadOnly }]);
    },
  });

  const {
    mutate: deleteMutate,
    isLoading: isDeleting,
    error: deleteError,
  } = useMutation({
    mutationFn: (notificationIds: string[]) => apiDeleteNotifications({ sessionToken, userId, notificationIds }),
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeys.Notifications, userId, { unread_only: unreadOnly }]);
    },
  });

  const markAsRead = (notificationIds: string[], options?: MarkAsReadOptions) => {
    markAsReadMutate(notificationIds, {
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    });
  };

  const deleteNotifications = (notificationIds: string[], options?: DeleteOptions) => {
    deleteMutate(notificationIds, {
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    });
  };

  const fetchById = async (notificationId: string) => {
    if (!userId || !sessionToken) return null;

    try {
      const response = await apiFetchNotificationById({
        sessionToken,
        userId,
        notificationId,
      });
      return response.notifications?.[0] || null;
    } catch {
      return null;
    }
  };

  const markAllAsRead = (options?: MarkAsReadOptions) => {
    const notifications = notificationsData?.notifications || [];
    const unreadIds = notifications.filter((notification) => !notification.is_read).map((notification) => notification.id);

    if (unreadIds.length > 0) {
      markAsRead(unreadIds, options);
    }
  };

  const deleteAll = (options?: DeleteOptions) => {
    const notifications = notificationsData?.notifications || [];
    const allIds = notifications.map((notification) => notification.id);

    if (allIds.length > 0) {
      deleteNotifications(allIds, options);
    }
  };

  const getUnreadCount = () => {
    if (!notificationsData?.notifications) return 0;
    return notificationsData.notifications.filter((notification) => !notification.is_read).length;
  };

  return {
    notifications: notificationsData?.notifications ?? [],
    isLoading,
    isError,
    error,
    refetchNotifications,

    markAsRead,
    deleteNotifications,
    fetchById,

    markAllAsRead,
    deleteAll,
    getUnreadCount,

    isMarkingAsRead,
    isDeleting,
    markAsReadError,
    deleteError,
  };
};

export const useNotificationById = (notificationId: string, enabled = true) => {
  const { authData } = useAuthState();
  const userId = useCurrentUserId();
  const sessionToken = authData?.session_token;

  return useQuery(
    [queryKeys.Notifications, 'byId', userId, notificationId],
    async () => {
      const response = await apiFetchNotificationById({
        sessionToken,
        userId,
        notificationId,
      });
      return response.notifications?.[0] || null;
    },
    {
      enabled: enabled && Boolean(userId && sessionToken && notificationId),
    },
  );
};
